/**
 * Player Designation Rules Configuration
 * 
 * This file imports sport-specific designation rules and creates the configuration
 * for generating player designations (characteristic traits like "Game Wrecker", 
 * "Field General", etc.) based on their attributes and positions.
 * 
 * Each sport's rules are defined in separate files (e.g., footballDesignations.ts)
 * to keep the code organized and maintainable.
 */

import type { Sport } from '../types';
import type { DesignationRule, PositionCategories } from '../utils/playerDesignations/types';
import { FOOTBALL_DESIGNATIONS, FOOTBALL_POSITION_CATEGORIES } from './designations/footballDesignations';

/**
 * Sport-specific designation configuration
 */
export interface PlayerDesignationConfig {
  sport: Sport;
  designations: DesignationRule[];
  positionCategories: PositionCategories;
  minMatchCount?: number; // Minimum number of matching attributes required (default: 3)
}

/**
 * Sport-specific player designation configurations
 * Each sport imports its rules from a separate file for better organization
 */
export const PLAYER_DESIGNATION_CONFIGS: Record<Sport, PlayerDesignationConfig> = {
  Football: {
    sport: 'Football',
    designations: FOOTBALL_DESIGNATIONS,
    positionCategories: FOOTBALL_POSITION_CATEGORIES,
    minMatchCount: 3,
  },
  FlagFootball: {
    sport: 'FlagFootball',
    designations: FOOTBALL_DESIGNATIONS, // Same as Football
    positionCategories: FOOTBALL_POSITION_CATEGORIES,
    minMatchCount: 3,
  },
  Baseball: {
    sport: 'Baseball',
    designations: [], // To be defined
    positionCategories: {
      offensive: [],
      defensive: [],
    },
    minMatchCount: 3,
  },
  Softball: {
    sport: 'Softball',
    designations: [], // To be defined
    positionCategories: {
      offensive: [],
      defensive: [],
    },
    minMatchCount: 3,
  },
  Basketball: {
    sport: 'Basketball',
    designations: [], // To be defined
    positionCategories: {
      offensive: [],
      defensive: [],
    },
    minMatchCount: 3,
  },
  Soccer: {
    sport: 'Soccer',
    designations: [], // To be defined
    positionCategories: {
      offensive: [],
      defensive: [],
    },
    minMatchCount: 3,
  },
  Volleyball: {
    sport: 'Volleyball',
    designations: [], // To be defined
    positionCategories: {
      offensive: [],
      defensive: [],
    },
    minMatchCount: 3,
  },
  Hockey: {
    sport: 'Hockey',
    designations: [], // To be defined
    positionCategories: {
      offensive: [],
      defensive: [],
    },
    minMatchCount: 3,
  },
  Lacrosse: {
    sport: 'Lacrosse',
    designations: [], // To be defined
    positionCategories: {
      offensive: [],
      defensive: [],
    },
    minMatchCount: 3,
  },
  Tennis: {
    sport: 'Tennis',
    designations: [], // To be defined
    positionCategories: {
      offensive: [],
      defensive: [],
    },
    minMatchCount: 3,
  },
};

/**
 * Get player designations for a player based on their attributes, positions, and sport
 * Returns offense and defense designations separately
 */
export function getPlayerDesignations(
  attributes: Array<{ attribute_key: string; value: number }>,
  positions: string[],
  roleTypes: string[],
  sport: Sport
): { offense_designation: string | null; defense_designation: string | null } {
  const config = PLAYER_DESIGNATION_CONFIGS[sport];
  if (!config || config.designations.length === 0) {
    return { offense_designation: null, defense_designation: null };
  }

  // Import core functions (using dynamic import to avoid circular dependencies)
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { findBestDesignation, getDesignationSide } = require('../utils/playerDesignations/core');
  
  // Attributes are already in the correct format
  const playerAttributes = attributes;

  // Separate designations by side
  const offenseDesignations = config.designations.filter(rule => {
    const side = getDesignationSide(rule, config.positionCategories);
    return side === 'offense';
  });

  const defenseDesignations = config.designations.filter(rule => {
    const side = getDesignationSide(rule, config.positionCategories);
    return side === 'defense';
  });

  // Find best designation for each side
  // Note: findBestDesignation expects PlayerAttribute[] format
  const offenseDesignation = findBestDesignation(
    offenseDesignations,
    playerAttributes as any, // Type assertion needed due to format compatibility
    positions,
    roleTypes
  );

  const defenseDesignation = findBestDesignation(
    defenseDesignations,
    playerAttributes as any, // Type assertion needed due to format compatibility
    positions,
    roleTypes
  );

  return {
    offense_designation: offenseDesignation,
    defense_designation: defenseDesignation,
  };
}

